import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Shield,
  Mic,
  AlertTriangle,
  Phone,
  FileText,
  EyeOff,
  Lock,
  UserX,
  ArrowRight,
  CheckCircle2,
  Zap,
  Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import EmergencyCallModal from "@/components/EmergencyCallModal";
import PreventivePlanHeroBanner from "@/components/PreventivePlanHeroBanner";
import LegalValidationBadge from "@/components/LegalValidationBadge";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [callOpen, setCallOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("call") === "1") setCallOpen(true);
  }, [searchParams]);

  const goAuth = () => navigate(user ? "/dashboard" : "/auth");
  const openCall = () => setCallOpen(true);
  const closeCall = () => {
    setCallOpen(false);
    if (searchParams.get("call")) {
      const next = new URLSearchParams(searchParams);
      next.delete("call");
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-28">
      {/* Top nav */}
      <header className="max-w-5xl mx-auto w-full px-5 pt-6 flex items-center justify-between">
        <BrandMark size="md" />
        <button
          onClick={goAuth}
          data-testid="landing-nav-login"
          className="text-sm font-bold text-zinc-700 hover:text-zinc-900 px-3 py-2"
        >
          {user ? "Mi cuenta" : "Entrar"}
        </button>
      </header>

      {/* HERO */}
      <section className="max-w-5xl mx-auto w-full px-5 pt-10 sm:pt-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1.5 mb-5">
              <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-widest text-red-700">
                Información inmediata · 24/7
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-zinc-900 leading-[0.95]">
              Cuando ICE
              <br />
              toque tu puerta,
              <br />
              <span className="text-red-600">tendrás voz.</span>
            </h1>

            <p className="text-base sm:text-lg text-zinc-700 mt-6 leading-relaxed max-w-lg">
              Plataforma de información legal por voz para inmigrantes en EE.UU. Te guía en vivo, paso a paso, en español. <span className="font-bold">100% anónima.</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button
                onClick={openCall}
                data-testid="landing-cta-primary"
                className="h-16 px-8 text-lg font-black bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-[0_10px_30px_rgba(220,38,38,0.35)] uppercase flex items-center gap-2"
              >
                Llamar a la IA ahora
                <ArrowRight className="w-5 h-5" />
              </Button>
              <button
                onClick={() => document.getElementById("escenarios")?.scrollIntoView({ behavior: "smooth" })}
                className="h-16 px-6 text-base font-bold text-zinc-900 hover:bg-zinc-100 rounded-xl border-2 border-zinc-300"
                data-testid="landing-cta-secondary"
              >
                ¿Cómo funciona?
              </button>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8">
              {[
                { Icon: CheckCircle2, text: "Sin nombre real" },
                { Icon: CheckCircle2, text: "Sin teléfono" },
                { Icon: CheckCircle2, text: "Sin dirección" },
              ].map(({ Icon, text }) => (
                <div key={text} className="flex items-center gap-1.5 text-sm font-semibold text-zinc-700">
                  <Icon className="w-4 h-4 text-emerald-600" />
                  {text}
                </div>
              ))}
            </div>
          </div>

          {/* Hero card visual */}
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-red-100 to-amber-100 rounded-3xl blur-2xl opacity-60" />
            <div className="relative bg-zinc-900 rounded-3xl p-7 shadow-2xl border-8 border-zinc-800">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                  Llamada en curso
                </span>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Tú dijiste</p>
                  <p className="text-white text-base font-medium">«ICE está en mi puerta…»</p>
                </div>
              </div>

              <div className="bg-zinc-800 rounded-2xl p-4 border-l-4 border-l-red-500">
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-1">Asistente</p>
                <p className="text-white text-base leading-relaxed font-medium">
                  Respira. No abras la puerta. Pregunta si tienen una orden firmada por un juez.
                </p>
              </div>

              <div className="flex items-center justify-center gap-1.5 mt-5 h-8">
                {[6, 14, 22, 10, 18, 8, 24, 12, 20, 6, 14, 18, 10, 22, 8].map((h, i) => (
                  <div
                    key={`hero-bar-${i}`}
                    className="w-1 bg-red-500 rounded-full opacity-80 wave-bar"
                    style={{ height: `${h}px`, animationDelay: `${i * 0.08}s` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PREVENTIVE PROTECTION PLAN BANNER */}
      <section className="max-w-5xl mx-auto w-full px-5 mt-12">
        <PreventivePlanHeroBanner />
        <div className="flex justify-center mt-3">
          <LegalValidationBadge variant="compact" />
        </div>
      </section>

      {/* PRIVACY SECTION */}
      <section className="max-w-5xl mx-auto w-full px-5 mt-20">
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-3xl p-7 sm:p-10">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 rounded-2xl bg-emerald-600 flex items-center justify-center flex-shrink-0">
              <EyeOff className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-black text-emerald-700 mb-1">
                Tu integridad migratoria es sagrada
              </p>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 leading-tight">
                Cero datos personales. Cero rastro.
              </h2>
            </div>
          </div>
          <p className="text-base text-zinc-800 leading-relaxed mb-6 max-w-2xl">
            En esta plataforma NO pedimos tu nombre real, ni tu número de teléfono, ni tu dirección, ni tu correo electrónico. Para protegerte, te registras con un <span className="font-bold">pseudónimo inventado</span> y una contraseña. Así nadie puede rastrearte. Ni el ICE, ni USCIS, ni ninguna autoridad migratoria.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { Icon: UserX, label: "Sin nombre real", desc: "Inventa un pseudónimo" },
              { Icon: Lock, label: "Cifrado total", desc: "Tu sesión es privada" },
              { Icon: Shield, label: "Nunca compartimos", desc: "Tus conversaciones son tuyas" },
            ].map(({ Icon, label, desc }) => (
              <div key={label} className="bg-white border border-emerald-200 rounded-xl p-4">
                <Icon className="w-6 h-6 text-emerald-700 mb-2" strokeWidth={2.5} />
                <p className="font-bold text-zinc-900 text-sm">{label}</p>
                <p className="text-xs text-zinc-600 mt-0.5">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCENARIOS */}
      <section id="escenarios" className="max-w-5xl mx-auto w-full px-5 mt-20">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-widest font-black text-red-600 mb-2">
            Te acompañamos en dos momentos críticos
          </p>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter text-zinc-900 leading-tight">
            Dos escenarios.
            <br />
            Una misma certeza.
          </h2>
        </div>

        {/* Scenario 1 */}
        <div className="bg-white rounded-3xl shadow-xl border border-zinc-200 overflow-hidden mb-8">
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="p-7 sm:p-10 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-red-100 px-3 py-1 rounded-full mb-4">
                <AlertTriangle className="w-4 h-4 text-red-700" />
                <span className="text-xs font-black uppercase tracking-widest text-red-700">
                  Escenario 1 · Emergencia
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 leading-tight mb-3">
                Cuando ICE te detiene, hablas con la IA al instante.
              </h3>
              <p className="text-base text-zinc-700 leading-relaxed mb-5">
                Entras a la plataforma, presionas el botón rojo y <span className="font-bold">hablas en vivo</span> con un asistente automatizado experto en tus derechos. Él te dice qué decir, qué NO firmar, qué pedir y cómo demostrar que conoces la ley. Todo por voz, en español, en segundos.
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  "Respuesta inmediata, sin esperar a nadie.",
                  "Te habla con voz calmada en español latino.",
                  "Frases cortas y claras, aunque estés en pánico.",
                  "Sabe si estás en la puerta, la calle, el trabajo o detenido.",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-zinc-800">
                    <Zap className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="leading-snug">{t}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={openCall}
                data-testid="landing-scenario1-cta"
                className="h-14 px-6 text-base font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl"
              >
                Activar mi escudo →
              </Button>
            </div>
            <div className="bg-gradient-to-br from-red-600 to-red-800 p-7 sm:p-10 flex items-center justify-center order-1 lg:order-2 relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-10 right-10 w-32 h-32 bg-white rounded-full blur-3xl" />
                <div className="absolute bottom-10 left-10 w-40 h-40 bg-red-300 rounded-full blur-3xl" />
              </div>
              <button
                type="button"
                onClick={openCall}
                data-testid="hero-emergency-card"
                className="relative w-full max-w-xs text-left group focus:outline-none focus:ring-4 focus:ring-white/40 rounded-3xl"
              >
                <div className="bg-white rounded-3xl p-6 shadow-2xl group-hover:scale-[1.02] group-active:scale-[0.99] transition-transform cursor-pointer">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-zinc-700">
                      Modo emergencia
                    </span>
                  </div>
                  <div className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center mx-auto mb-4 emergency-pulse shadow-xl">
                    <Mic className="w-10 h-10 text-white" />
                  </div>
                  <p className="text-center text-zinc-900 font-black text-lg leading-tight mb-2">
                    Toca para hablar
                  </p>
                  <p className="text-center text-zinc-500 text-xs">
                    El asistente te responde en 2 segundos
                  </p>
                  <div className="flex items-center justify-center gap-1 mt-4 h-6">
                    {[8, 16, 24, 12, 20, 10, 18, 14].map((h, i) => (
                      <div
                        key={`emerg-bar-${i}`}
                        className="w-1 bg-red-500 rounded-full wave-bar"
                        style={{ height: `${h}px`, animationDelay: `${i * 0.1}s` }}
                      />
                    ))}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Scenario 2 */}
        <div className="bg-white rounded-3xl shadow-xl border border-zinc-200 overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="bg-gradient-to-br from-blue-700 to-blue-900 p-7 sm:p-10 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl" />
                <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-300 rounded-full blur-3xl" />
              </div>
              <div className="relative w-full max-w-xs">
                <div className="bg-white rounded-3xl p-6 shadow-2xl">
                  <div className="flex items-center gap-2 mb-4">
                    <Phone className="w-4 h-4 text-blue-700" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-700">
                      Llamada telefónica
                    </span>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="bg-blue-50 border-l-4 border-l-blue-700 p-3 rounded-r-lg">
                      <p className="text-[10px] font-bold uppercase text-blue-700 mb-0.5">Paso 1 de 7</p>
                      <p className="text-sm text-zinc-900 font-semibold leading-snug">
                        Formulario I-589: Solicitud de asilo
                      </p>
                    </div>
                    <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-lg">
                      <p className="text-xs text-zinc-700 leading-snug">
                        «Lee la pregunta A.2 y dime tu fecha de nacimiento. Yo la escribo por ti.»
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-blue-700 text-xs font-bold">
                    <FileText className="w-4 h-4" />
                    EOIR-28, EOIR-26, I-589
                  </div>
                </div>
              </div>
            </div>
            <div className="p-7 sm:p-10">
              <div className="inline-flex items-center gap-2 bg-blue-100 px-3 py-1 rounded-full mb-4">
                <Phone className="w-4 h-4 text-blue-700" />
                <span className="text-xs font-black uppercase tracking-widest text-blue-700">
                  Escenario 2 · Centro de detención
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 leading-tight mb-3">
                Te llamamos por teléfono y llenamos tus formularios juntos.
              </h3>
              <p className="text-base text-zinc-700 leading-relaxed mb-5">
                Ya estás en un centro de detención. Nos llamas desde el teléfono autorizado del centro. Activamos los <span className="font-bold">Manuales Oficiales de Autoayuda de Inmigración</span> (PBNDS 2011 §&nbsp;6.3) y te guiamos <span className="font-bold">paso a paso</span> a llenar cada formulario de la corte EOIR para defender tus derechos.
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  "Lectura en voz alta de los manuales en español sencillo.",
                  "Te guiamos a llenar I-589 (asilo), EOIR-28, EOIR-26 y más.",
                  "Te explicamos audiencia de fianza, audiencia maestra y apelación.",
                  "Traducimos los términos legales en inglés a palabras claras.",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-zinc-800">
                    <CheckCircle2 className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
                    <span className="leading-snug">{t}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={goAuth}
                data-testid="landing-scenario2-cta"
                className="h-14 px-6 text-base font-bold bg-blue-800 hover:bg-blue-900 text-white rounded-xl"
              >
                Conocer el servicio →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="max-w-5xl mx-auto w-full px-5 mt-20">
        <div className="bg-zinc-900 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600 rounded-full blur-3xl" />
          </div>
          <div className="relative">
            <Heart className="w-10 h-10 text-red-500 mx-auto mb-4" strokeWidth={2} />
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight mb-3">
              No estás solo.
              <br />
              Conoces tus derechos.
            </h3>
            <p className="text-base text-zinc-300 leading-relaxed max-w-xl mx-auto mb-6">
              Basado en los Estándares Nacionales de Detención del ICE (PBNDS 2011 §6.3 «Law Libraries and Legal Material») y el derecho constitucional a la auto-representación civil.
            </p>
            <Button
              onClick={goAuth}
              data-testid="landing-cta-final"
              className="h-16 px-10 text-lg font-black bg-white hover:bg-zinc-100 text-zinc-900 rounded-xl uppercase"
            >
              Crear mi cuenta anónima
            </Button>
            <p className="text-xs text-zinc-500 mt-4">
              Listo en 30 segundos. Sin tarjeta. Sin datos personales.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-5xl mx-auto w-full px-5 mt-16 pb-8">
        <div className="border-t border-zinc-200 pt-6 text-center">
          <div className="flex justify-center mb-4">
            <BrandMark size="sm" />
          </div>
          <div className="flex justify-center gap-4 mb-4 flex-wrap">
            <button
              onClick={() => navigate("/quienes-somos")}
              data-testid="landing-footer-quienes"
              className="text-sm font-bold text-zinc-700 hover:text-zinc-900 underline"
            >
              Quiénes somos
            </button>
            <button
              onClick={() => navigate("/precios")}
              data-testid="landing-footer-precios"
              className="text-sm font-bold text-zinc-700 hover:text-zinc-900 underline"
            >
              Precios
            </button>
            <button
              onClick={() => navigate("/aviso-de-privacidad")}
              data-testid="landing-footer-aviso"
              className="text-sm font-bold text-zinc-700 hover:text-zinc-900 underline"
            >
              Aviso de Privacidad
            </button>
            <button
              onClick={() => navigate("/terminos-y-condiciones")}
              data-testid="landing-footer-terminos"
              className="text-sm font-bold text-zinc-700 hover:text-zinc-900 underline"
            >
              Términos y Condiciones
            </button>
            <button
              onClick={() => navigate("/admin")}
              data-testid="landing-footer-admin"
              className="text-xs font-bold text-zinc-400 hover:text-zinc-700"
            >
              Acceso administrador
            </button>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed max-w-2xl mx-auto">
            <span className="font-bold">miapoyo.com · Defiende Tus Derechos</span> es operado por <span className="font-bold">MARKETING DE INTELIGENCIA ARTIFICIAL S. de R.L. de C.V.</span>, con domicilio en Av. Américas 1545, pisos 20, 21 y 22, Guadalajara, Jalisco, México. Asistente automatizado de educación legal. No somos abogados ni prestamos servicios legales. Información de carácter educativo basada en fuentes oficiales públicas. Actúa bajo PBNDS 2011 §6.3 y el derecho constitucional a la auto-representación civil pro se.
          </p>
        </div>
      </footer>

      <DisclaimerBanner />
      <EmergencyCallModal open={callOpen} onClose={closeCall} />
    </div>
  );
}
